/** Strip query/hash so drill depth compares path only. */
export function routePathOnly(path: string): string {
  return path.split(/[?#]/)[0] ?? path;
}

export type DrillDirection = "forward" | "back" | "none";

/** Nesting depth for invoice upload drill-in (list → batch → line). */
export function invoiceUploadDrillDepth(path: string): number {
  const p = routePathOnly(path);
  if (/^\/invoices\/batches\/[^/]+\/lines\/[^/]+$/.test(p)) return 2;
  if (/^\/invoices\/batches\/[^/]+$/.test(p)) return 1;
  return 0;
}

/**
 * Whether `path` is a detail/drill-in route for a known list parent (not
 * multi-segment create flows like `/rates/create/from-document`).
 */
function isBookingDetail(path: string): boolean {
  return /^\/bookings\/[^/]+$/.test(path);
}

function isRateDetail(path: string): boolean {
  const p = routePathOnly(path);
  if (!/^\/rates\/[^/]+$/.test(p)) return false;
  return !p.startsWith("/rates/create");
}

function isReportDetail(path: string): boolean {
  const p = routePathOnly(path);
  return p.startsWith("/reports/") && p !== "/reports";
}

/**
 * Detect list → detail navigation for staggered enter/exit motion. Used by
 * {@link PageTransition} for invoices (batch stack), bookings, rates, reports.
 */
export function drillDirection(prevPath: string, nextPath: string): DrillDirection {
  const prev = routePathOnly(prevPath);
  const next = routePathOnly(nextPath);

  if (prev === next) return "none";

  const invoicePrev = invoiceUploadDrillDepth(prev);
  const invoiceNext = invoiceUploadDrillDepth(next);
  if (invoicePrev > 0 || invoiceNext > 0) {
    if (invoiceNext > invoicePrev) return "forward";
    if (invoiceNext < invoicePrev) return "back";
  }

  if (isBookingDetail(next) && prev === "/bookings") return "forward";
  if (isBookingDetail(prev) && next === "/bookings") return "back";

  if (isRateDetail(next) && prev === "/rates") return "forward";
  if (isRateDetail(prev) && next === "/rates") return "back";

  if (isReportDetail(next) && prev === "/reports") return "forward";
  if (isReportDetail(prev) && next === "/reports") return "back";

  return "none";
}
